import {
  Box,
  ButtonBase,
  CssBaseline,
  Drawer,
  Grid,
  List,
  capitalize,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { MemoryTwoTone, ScienceTwoTone } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'

import BiotechTwoTone from '@mui/icons-material/BiotechTwoTone'
import CorporateFareTwoTone from '@mui/icons-material/CorporateFareTwoTone'
import DynamicFormTwoTone from '@mui/icons-material/DynamicFormTwoTone'
import GroupTwoTone from '@mui/icons-material/GroupTwoTone'
import HolloidSvg from '../common/HolloidSvg'
import InsightsTwoTone from '@mui/icons-material/InsightsTwoTone'
import LogoSvg from '../common/LogoSvg'
import MenuItem from './MenuItem'
import PermDataSettingTwoTone from '@mui/icons-material/PermDataSettingTwoTone'
import PrecisionManufacturingTwoTone from '@mui/icons-material/PrecisionManufacturingTwoTone'
import RouterTwoTone from '@mui/icons-material/RouterTwoTone'
import ScatterPlotTwoTone from '@mui/icons-material/ScatterPlotTwoTone'
import SummarizeTwoTone from '@mui/icons-material/SummarizeTwoTone'
import manageAccountsTwoTone from '@mui/icons-material/ManageAccountsTwoTone'
import { resolveNavPath } from '../../utilities/pathMapping'
import { showHardwareAndHollometers } from '../../utilities/featureFlags'
import { useState } from 'react'
import { useUser } from '../../context/CurrentUserContext'

const menuItems = [
  {
    id: 'data-viewer',
    text: 'Data Viewer',
    Icon: InsightsTwoTone,
    roles: ['admin', 'organization_admin'],
  },
  {
    id: 'analyses',
    text: 'Analyses',
    Icon: ScatterPlotTwoTone,
    roles: ['admin'],
  },
  {
    id: 'analysis-chains',
    text: 'Analysis Chains',
    Icon: PrecisionManufacturingTwoTone,
    roles: ['admin'],
  },
  {
    id: 'manual-trigger',
    text: 'Manual trigger',
    Icon: DynamicFormTwoTone,
    roles: ['admin'],
  },
  {
    id: 'data-types',
    text: 'Data Types',
    Icon: SummarizeTwoTone,
    roles: ['admin'],
  },
  {
    id: 'devices',
    text: 'Devices',
    Icon: ScienceTwoTone,
    roles: ['admin', 'organization_admin'],
  },
  {
    id: resolveNavPath('stations'),
    text: capitalize(resolveNavPath('stations')),
    Icon: RouterTwoTone,
    roles: ['admin', 'supervisor', 'technician'],
  },
  {
    id: 'hardware',
    text: 'Hardware',
    Icon: MemoryTwoTone,
    roles: ['admin', 'supervisor', 'technician'],
  },
  {
    id: 'experiments',
    text: 'Experiments',
    Icon: BiotechTwoTone,
    roles: ['admin', 'organization_admin', 'user'],
  },
  {
    id: 'plot-config',
    text: 'Plots',
    Icon: PermDataSettingTwoTone,
    roles: ['admin'],
  },
  {
    id: 'organizations',
    text: 'Organizations',
    Icon: CorporateFareTwoTone,
    roles: ['admin'],
  },
  {
    id: 'users',
    text: 'Users',
    Icon: GroupTwoTone,
    roles: ['admin'],
  },
  {
    id: 'roles',
    text: 'Roles',
    Icon: manageAccountsTwoTone,
    roles: ['admin'],
  },
]

function SideMenu({
  onMenuItemClick,
  mobileOpen,
  onDrawerToggle,
  width,
  collapsedWidth,
  onCollapse,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isCollapsed, setIsCollapsed] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const { user } = useUser()

  const handleMenuItemClick = (itemId) => {
    navigate(`/${itemId}`)
    if (onDrawerToggle && isMobile) {
      onDrawerToggle()
    }
    if (onMenuItemClick) {
      onMenuItemClick(itemId)
    }
  }

  const hasPermission = (itemRoles) => {
    if (!user || !user.role) return false
    return itemRoles.some((role) => user.role === role)
  }

  const filteredMenuItems = menuItems.filter((item) => {
    const isHardwareOrHollometer =
      item.id === 'hardware' || item.id === resolveNavPath('stations')
    if (isHardwareOrHollometer && !showHardwareAndHollometers) return false
    return hasPermission(item.roles)
  })

  // Function to determine if a menu item is active
  const isActive = (itemId, isSubItem = false) => {
    if (isSubItem) {
      // For sub-items, check if the pathname exactly matches the sub-item's id
      return location.pathname === `/${itemId}`
    }
    // For non-sub-items, check if the pathname exactly matches or if the pathname is the base of a sub-path
    const pathMatch =
      location.pathname === `/${itemId}` ||
      location.pathname.startsWith(`/${itemId}/`)
    const hasSubItems =
      menuItems.find((item) => item.id === itemId)?.subItems?.length > 0
    return pathMatch && !hasSubItems
  }

  const drawer = (
    <Grid
      container
      component="nav"
      sx={{
        minHeight: '100%',
        flexDirection: 'column',
        flexWrap: 'nowrap',
      }}
    >
      {/* Logo */}
      <Grid size={12} height={65} sx={{ flexShrink: 0 }}>
        <ButtonBase
          onClick={() => {
            setIsCollapsed(!isCollapsed)
            onCollapse()
          }}
          aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            pt: 1,
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.menu.text.default}`,
              outlineOffset: '-4px',
            },
          }}
        >
          {isCollapsed && !isMobile ? (
            <HolloidSvg
              color={theme.palette.menu.text.default}
              sx={{
                height: '60%',
                width: '100%',
                pb: 1,
              }}
            />
          ) : (
            <LogoSvg
              color={theme.palette.menu.text.default}
              sx={{
                height: '60%',
                width: '100%',
                pb: 1,
              }}
            />
          )}
        </ButtonBase>
      </Grid>

      {/* Menus */}
      <Grid
        size={12}
        sx={{
          overflowY: 'auto',
          flexGrow: 1,
        }}
      >
        <List
          sx={{
            p: 0,
          }}
        >
          {filteredMenuItems.map((item) => (
            <MenuItem
              key={item.id}
              id={item.id}
              Icon={item.Icon}
              text={item.text}
              subItems={
                item.subItems
                  ? item.subItems.filter((subItem) =>
                      hasPermission(subItem.roles),
                    )
                  : undefined
              }
              onClick={handleMenuItemClick}
              isActive={isActive(item.id)}
              isSubItem={false}
              isCollapsed={isCollapsed}
            />
          ))}
        </List>
      </Grid>
    </Grid>
  )

  const drawerWidth = isCollapsed && !isMobile ? collapsedWidth : width

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidth },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
        flexShrink: { sm: 0 },
      }}
    >
      <CssBaseline />
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            top: 0,
            height: '100%',
            backgroundColor: theme.palette.menu.background.default,
            border: 'none',
          },
          zIndex: theme.zIndex.appBar + 1,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            border: 'none',
            width: drawerWidth,
            backgroundColor: theme.palette.menu.background.default,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  )
}

export default SideMenu
